import { useSnapshot } from "valtio";
import { Outlet, useNavigate } from "react-router-dom";
import { createContext, useEffect, useState } from "react";

import { User } from "../types/user";
import { Routes } from "./baseRoutes";
import { Dashboard } from "../types/dashboard";
import { useAccountInfo } from "../hooks/useAccountInfo";
import {
  Meta,
  dashboardStore,
  metaStore,
  userStore,
  wellWishesStore,
} from "../hooks/store";
import { ensureLightMode } from "./ensure.light.mode";

import { WellWish } from "../types/wellwish";
import { WSMessage } from "../utils/websocket";
import { UserWeddingRole } from "../types/wedding";
import { useWebSocketSetup } from "../hooks/useWebsocketSetup";
import { ClientStorage, ClientStorageItem } from "../utils/clientstorage";
import { getBasicWeddingInformation } from "./utils";
import { tokenExpiredSync } from "./AuthRoutesProvider";

type ViewContext = "Editor" | "Preview";

export const CurrentAccountInfoContext = createContext<{
  meta: Meta;
  user: User;
  viewContext: ViewContext;
  setViewContext: (viewContext: ViewContext) => void;
  dashboard: Dashboard;
  wellWishes: {
    wellWishesCount: number;
    wellWishes: WellWish[];
  };
  setUser: (user: User) => void;
  setDashboard: (dashboard: Dashboard) => void;
  setWellWishes: (wellWishes: {
    wellWishesCount: number;
    wellWishes: WellWish[];
  }) => void;
  setMeta: (meta: Meta) => void;
  sendMessage: (wsmsg: WSMessage) => void;
}>({} as any);

// interface EventPlannerRouteProviderProps {
//   children: React.ReactNode;
// }

const EventPlannerRouteProvider = () => {
  const navigate = useNavigate();
  const XAccountHash = ClientStorage.fetch(ClientStorageItem.XAccountHash);
  const userWeddingRole = ClientStorage.fetch(
    ClientStorageItem.UserWeddingRole,
  ) as UserWeddingRole;
  const { user, dashboard, wellWishes } = useAccountInfo(
    userWeddingRole,
    XAccountHash,
  );
  const [viewContext, setViewContext] = useState<ViewContext>("Preview");
  const { showOnboardingModal } = useSnapshot(metaStore);
  const { sendMessage } = useWebSocketSetup();

  // TODO: useEffect hook to check for the cookie on mount
  useEffect(() => {
    ensureLightMode();
    if (XAccountHash) {
      getBasicWeddingInformation(XAccountHash);
    } else {
      // we prolly should log the user out.
      tokenExpiredSync();
      navigate(Routes.login);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [XAccountHash]);

  const setUser = (currentUser: User) => {
    userStore.data = currentUser;
  };

  const setDashboard = (currentDashboard: Dashboard) => {
    dashboardStore.data = currentDashboard;
  };


  const setWellWishes = (currentWellWishes: {
    wellWishesCount: number;
    wellWishes: WellWish[];
  }) => {
    wellWishesStore.data = {
      ...wellWishesStore.data,
      ...currentWellWishes,
    };
  };

  const setMeta = (meta: Meta) => {
    metaStore.showOnboardingModal = meta.showOnboardingModal;
  };

  return (
    <CurrentAccountInfoContext.Provider
      value={{
        meta: {
          showOnboardingModal: showOnboardingModal,
        },
        user,
        viewContext,
        setViewContext,
        dashboard,
        setUser,
        setDashboard,
        setMeta,
        wellWishes,
        setWellWishes,
        sendMessage,
      }}
    >
      {/* {children} */}
      <Outlet />
    </CurrentAccountInfoContext.Provider>
  );
};

export default EventPlannerRouteProvider;
